import React, { useState, useRef, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    TextInput,
    Animated,
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform,
    Keyboard,
    TouchableWithoutFeedback,
} from 'react-native';
import SimpleHeader from '../../components/SimpleHeader';
import PINDots from '../../components/UIComponents/PINDots';
import PasswordInput from '../../components/UIComponents/PasswordInput';
import PinKeypad, { PIN_INPUT_PROPS } from '../../../web/PinKeypad';
import { usePIN } from '../../contexts/PINContext';
import { useAuth } from '../../contexts/AuthContext';
import { v2Login } from '../../services/v2/api';
import { COLORS } from '../../theme/themeTokens';

const PIN_LENGTH = 4;
const MAX_PASSWORD_ATTEMPTS = 3;

type Step = 'currentPIN' | 'password' | 'newPIN';

interface ChangePINScreenProps {
    onBack: () => void;
}

const ChangePINScreen: React.FC<ChangePINScreenProps> = ({ onBack }) => {
    const { verifyPIN, setupPIN } = usePIN();
    const { user } = useAuth();

    const [step, setStep] = useState<Step>('currentPIN');

    const [currentPIN, setCurrentPIN] = useState('');
    const [pinError, setPinError] = useState<string | null>(null);
    const [pinAttempts, setPinAttempts] = useState(0);

    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordAttempts, setPasswordAttempts] = useState(0);
    const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);

    const [newPIN, setNewPIN] = useState('');
    const [newPINError, setNewPINError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const pinInputRef = useRef<TextInput>(null);
    const newPINInputRef = useRef<TextInput>(null);
    const shakeAnimation = useRef(new Animated.Value(0)).current;
    const newPINShakeAnimation = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const timer = setTimeout(() => pinInputRef.current?.focus(), 300);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (step === 'newPIN') {
            const timer = setTimeout(() => newPINInputRef.current?.focus(), 300);
            return () => clearTimeout(timer);
        }
    }, [step]);

    const triggerShake = (anim: Animated.Value) => {
        Animated.sequence([
            Animated.timing(anim, { toValue: 10, duration: 50, useNativeDriver: true }),
            Animated.timing(anim, { toValue: -10, duration: 50, useNativeDriver: true }),
            Animated.timing(anim, { toValue: 10, duration: 50, useNativeDriver: true }),
            Animated.timing(anim, { toValue: 0, duration: 50, useNativeDriver: true }),
        ]).start();
    };

    const handleCurrentPINChange = async (value: string) => {
        if (value.length > PIN_LENGTH) return;
        setCurrentPIN(value);
        setPinError(null);

        if (value.length === PIN_LENGTH) {
            const isValid = await verifyPIN(value);
            if (isValid) {
                setStep('newPIN');
            } else {
                const nextAttempts = pinAttempts + 1;
                setPinAttempts(nextAttempts);
                setCurrentPIN('');
                triggerShake(shakeAnimation);

                if (nextAttempts >= 3) {
                    setPinError(null);
                    setStep('password');
                } else {
                    setPinError('Incorrect PIN. Try again.');
                }
            }
        }
    };

    const handlePasswordContinue = async () => {
        if (!password.trim() || isVerifyingPassword) return;
        Keyboard.dismiss();
        setIsVerifyingPassword(true);
        setPasswordError(null);
        try {
            await v2Login(user?.login ?? '', password);
            setStep('newPIN');
        } catch {
            const next = passwordAttempts + 1;
            setPasswordAttempts(next);
            if (next >= MAX_PASSWORD_ATTEMPTS) {
                onBack();
            } else {
                setPasswordError('Incorrect password. Try again.');
                setPassword('');
            }
        } finally {
            setIsVerifyingPassword(false);
        }
    };

    const handleNewPINChange = (value: string) => {
        if (value.length > PIN_LENGTH) return;
        setNewPIN(value);
        setNewPINError(null);
        setSaveSuccess(false);
    };

    const handleSaveNewPIN = async () => {
        if (newPIN.length !== PIN_LENGTH || isSaving) return;
        setIsSaving(true);
        try {
            // setupPIN answers false when the Keychain refused the new PIN —
            // not an exception — and the screen reported success regardless.
            const saved = await setupPIN(newPIN);
            if (!saved) {
                throw new Error('Failed to save PIN');
            }
            setSaveSuccess(true);
            setTimeout(() => onBack(), 1200);
        } catch {
            setNewPINError('Failed to save PIN. Try again.');
            triggerShake(newPINShakeAnimation);
        } finally {
            setIsSaving(false);
        }
    };

    const renderCurrentPINStep = () => (
        <TouchableWithoutFeedback onPress={() => pinInputRef.current?.focus()}>
            <View style={styles.stepWrapper}>
                <View style={styles.topContent}>
                    <Text style={styles.stepTitle}>Enter your current PIN</Text>
                    <TextInput
                        ref={pinInputRef}
                        value={currentPIN}
                        onChangeText={handleCurrentPINChange}
                        keyboardType="number-pad"
                        maxLength={PIN_LENGTH}
                        style={styles.hiddenInput}
                        caretHidden
                        {...PIN_INPUT_PROPS}
                    />
                    <PINDots
                        pin={currentPIN}
                        pinLength={PIN_LENGTH}
                        error={pinError}
                        shakeAnimation={shakeAnimation}
                        onPress={() => {
                            pinInputRef.current?.blur();
                            setTimeout(() => pinInputRef.current?.focus(), 50);
                        }}
                    />
                    {pinError ? <Text style={styles.errorText}>{pinError}</Text> : null}

                    <PinKeypad value={currentPIN} length={PIN_LENGTH} onChange={handleCurrentPINChange} style={styles.keypad} />

                    <TouchableOpacity
                        style={styles.forgotPINLink}
                        onPress={() => {
                            setPinError(null);
                            setCurrentPIN('');
                            setStep('password');
                        }}
                        activeOpacity={0.7}>
                        <Text style={styles.bottomLinkText}>Forgot PIN?</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </TouchableWithoutFeedback>
    );

    const renderPasswordStep = () => (
        <KeyboardAvoidingView
            style={styles.stepWrapper}
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
            <View style={styles.topContent}>
                <Text style={styles.stepTitle}>Enter your password</Text>

                <View style={styles.passwordInputWrapper}>
                    <PasswordInput
                        value={password}
                        onChangeText={(v) => {
                            setPassword(v);
                            setPasswordError(null);
                        }}
                        placeholder="Password"
                    />
                </View>

                {passwordError ? <Text style={styles.errorText}>{passwordError}</Text> : null}

                <TouchableOpacity
                    style={[styles.primaryButton, (!password.trim() || isVerifyingPassword) && styles.primaryButtonDisabled]}
                    onPress={handlePasswordContinue}
                    disabled={!password.trim() || isVerifyingPassword}
                    activeOpacity={0.8}>
                    {isVerifyingPassword
                        ? <ActivityIndicator color={COLORS.white} />
                        : <Text style={styles.primaryButtonText}>Continue</Text>}
                </TouchableOpacity>
            </View>

            <TouchableOpacity
                style={styles.bottomLink}
                onPress={() => {
                    setPassword('');
                    setPasswordError(null);
                    setStep('currentPIN');
                    setTimeout(() => pinInputRef.current?.focus(), 300);
                }}
                activeOpacity={0.7}>
                <Text style={styles.bottomLinkText}>Enter PIN</Text>
            </TouchableOpacity>
        </KeyboardAvoidingView>
    );

    const renderNewPINStep = () => (
        <TouchableWithoutFeedback onPress={() => newPINInputRef.current?.focus()}>
            <View style={styles.stepWrapper}>
                <View style={styles.topContent}>
                    <Text style={styles.stepTitle}>Enter your new PIN</Text>
                    <TextInput
                        ref={newPINInputRef}
                        value={newPIN}
                        onChangeText={handleNewPINChange}
                        keyboardType="number-pad"
                        maxLength={PIN_LENGTH}
                        style={styles.hiddenInput}
                        caretHidden
                        {...PIN_INPUT_PROPS}
                    />
                    <PINDots
                        pin={newPIN}
                        pinLength={PIN_LENGTH}
                        error={newPINError}
                        shakeAnimation={newPINShakeAnimation}
                        onPress={() => {
                            newPINInputRef.current?.blur();
                            setTimeout(() => newPINInputRef.current?.focus(), 50);
                        }}
                    />
                    {newPINError ? <Text style={styles.errorText}>{newPINError}</Text> : null}

                    <PinKeypad
                        value={newPIN}
                        length={PIN_LENGTH}
                        onChange={handleNewPINChange}
                        disabled={isSaving}
                        style={styles.keypad}
                    />

                    <TouchableOpacity
                        style={[styles.primaryButton, (newPIN.length !== PIN_LENGTH || isSaving) && styles.primaryButtonDisabled]}
                        onPress={handleSaveNewPIN}
                        disabled={newPIN.length !== PIN_LENGTH || isSaving}
                        activeOpacity={0.8}>
                        {isSaving
                            ? <ActivityIndicator color={COLORS.white} />
                            : <Text style={styles.primaryButtonText}>Change PIN</Text>}
                    </TouchableOpacity>

                    {saveSuccess ? <Text style={styles.successText}>PIN updated successfully.</Text> : null}
                </View>
            </View>
        </TouchableWithoutFeedback>
    );

    return (
        <View style={styles.container}>
            <SimpleHeader title="Change PIN Code" onBack={onBack} backgroundVariant="white" />
            {step === 'currentPIN' && renderCurrentPINStep()}
            {step === 'password' && renderPasswordStep()}
            {step === 'newPIN' && renderNewPINStep()}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.white,
    },
    stepWrapper: {
        flex: 1,
        justifyContent: 'space-between',
        paddingBottom: 40,
    },
    topContent: {
        alignItems: 'center',
        paddingTop: 48,
        paddingHorizontal: 24,
    },
    stepTitle: {
        fontFamily: 'Inter',
        fontSize: 18,
        fontWeight: '500',
        color: COLORS.deepNavy,
        textAlign: 'center',
        marginBottom: 32,
    },
    hiddenInput: {
        position: 'absolute',
        opacity: 0,
        width: 0,
        height: 0,
    },
    keypad: {
        marginTop: 24,
        paddingHorizontal: 0,
    },
    errorText: {
        fontFamily: 'Inter',
        fontSize: 15,
        color: '#C94021',
        textAlign: 'center',
        marginTop: 4,
    },
    successText: {
        fontFamily: 'Inter',
        fontSize: 15,
        color: '#27AE60',
        textAlign: 'center',
        marginTop: 16,
    },
    passwordInputWrapper: {
        width: '100%',
        marginBottom: 4,
    },
    primaryButton: {
        height: 52,
        width: '100%',
        borderRadius: 12,
        backgroundColor: '#C94021',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 28,
    },
    primaryButtonDisabled: {
        opacity: 0.5,
    },
    primaryButtonText: {
      lineHeight: 22,
      fontFamily: 'Inter',
      fontSize: 16,
      fontWeight: '500',
        color: COLORS.white,
    },
    bottomLink: {
        alignSelf: 'center',
    },
    forgotPINLink: {
        alignSelf: 'center',
        marginTop: 24,
    },
    bottomLinkText: {
        fontFamily: 'Inter',
        fontSize: 16,
        fontWeight: '500',
        color: COLORS.deepNavy,
    },
});

export default ChangePINScreen;
